// Third Party
import * as React from 'react';
import cx from 'classnames';
import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
  FocusedInputShape,
  isInclusivelyAfterDay,
} from 'react-dates';
import 'react-dates/initialize';
import moment from 'moment';

// Utils
import { scrollToTop } from '@utils/scroll-to-top';
import { useSearch } from '@contexts/search-context';
import { routes } from '@routes';

// UI
import { ListingCard } from '@components/listing-card';
import { Typography } from '@components/typography';
import { Link } from '@components/link';
import { Icon, IconNames } from '@components/icon';
import { Spinner, SpinnerSize } from '@components/spinner';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { Dropdown } from '@components/dropdown';
import { Input } from '@components/form/input';

// TODO - move to components
import { FeaturesModal } from '@pages-components/search/search-map/features-modal';
import { concatenateAndReplace } from '@utils/test-helpers';

interface IHandleDatesChange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

type Props = {
  accountSlug: string;
};

// Cast DayPickerRangeController to ComponentType to resolve React JSX type conflicts
// This tells TypeScript to treat it as a valid React component that can accept props
const DayPickerComponent =
  DayPickerRangeController as React.ComponentType<DayPickerRangeControllerShape>;

export const AccountShowList: React.FC<Props> = ({ accountSlug }) => {
  const {
    checkInDate,
    checkOutDate,
    listingsLoading,
    listingsQueryResults,
    minNumberOfBathrooms,
    minNumberOfRooms,
    propertyTypes,
    uniquePropertyTypes,
    setUniquePropertyTypes,
    resetPagination,
    setCheckInDate,
    setCheckOutDate,
    setMinNumberOfBathrooms,
    setMinNumberOfRooms,
    setPropertyTypes,
    activateFeaturesModal,
    activeFilters,
    setActiveFilters,
    nextPage,
    prevPage,
  } = useSearch();

  const hasFiltersApplied =
    activeFilters.length > 0 ||
    minNumberOfBathrooms > 0 ||
    minNumberOfRooms ||
    propertyTypes.length > 0 ||
    uniquePropertyTypes.length > 0;

  const clearDates = (): void => {
    setCheckInDate(null);
    setCheckOutDate(null);
  };

  const resetFilters = (): void => {
    setPropertyTypes([]);
    setUniquePropertyTypes([]);
    setActiveFilters([]);
    setMinNumberOfRooms(0);
    setMinNumberOfBathrooms(0);
  };

  const dateRangePickerInputRef = React.useRef<HTMLDivElement>(null);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] =
    React.useState<FocusedInputShape | null>(null);

  const handleDatesChange = ({ startDate, endDate }: IHandleDatesChange) => {
    setCheckInDate && setCheckInDate(startDate);
    setCheckInDate(startDate);
    setCheckOutDate && setCheckOutDate(endDate);
    setCheckOutDate(endDate);
  };

  const roomInputRef = React.useRef<HTMLDivElement>(null);
  const [roomFocusedInput, setRoomFocusedInput] = React.useState(false);

  if (
    listingsQueryResults?.listingsSearch.__typename ===
    'ValidationErrorCollection'
  ) {
    return <div>Something went wrong</div>;
  }

  return (
    <>
      <FeaturesModal
        viewAllProperties={(filters) => {
          setActiveFilters(filters);
          resetPagination();
        }}
      />

      <div className="relative">
        <div className="flex flex-row justify-between align-middle items-center mt-8 space-x-4">
          {/* Filters */}
          <div className="hidden lg:flex flex-row w-full items-center justify-center align-middle whitespace-nowrap space-x-2 py-4">
            <div className="flex whitespace-nowrap space-x-2">
              {/* Date Filter */}
              <div className="min-w-[125px]">
                <Input
                  label="Check-in"
                  name="checkInDate"
                  data-testid={'@account/check-in-date'}
                  value={checkInDate?.format('MMM DD')}
                  onFocus={() => {
                    setDateRangePickerFocusedInput('startDate');
                  }}
                  leftContent={
                    <Icon
                      name={IconNames.CALENDAR_RANGE}
                      className="text-primary-main"
                    />
                  }
                  rightContent={
                    checkInDate ? (
                      <ButtonIcon
                        variant="text"
                        color="gray"
                        size="sm"
                        icon={IconNames.CIRCLE_X}
                        iconSize="lg"
                        ariaDescribedby="clear check in date input"
                        onClick={() => {
                          setCheckInDate && setCheckInDate(null);
                          setCheckInDate(null);
                          setDateRangePickerFocusedInput('startDate');
                        }}
                        className="ps-0 pe-0"
                      />
                    ) : (
                      <ButtonIcon
                        variant="text"
                        // @ts-ignore
                        color={'white'}
                        size="sm"
                        className="ps-0 pe-0"
                      />
                    )
                  }
                  autoComplete="off"
                  hasCaret={false}
                />
              </div>
              <div className="min-w-[125px]">
                <Input
                  label="Check-out"
                  name="checkOutDate"
                  data-testid={'@account/check-out-date'}
                  value={checkOutDate?.format('MMM DD')}
                  onFocus={() => {
                    if (!checkInDate) {
                      setDateRangePickerFocusedInput('startDate');
                    } else {
                      setDateRangePickerFocusedInput('endDate');
                    }
                  }}
                  leftContent={
                    <Icon
                      name={IconNames.CALENDAR_RANGE}
                      className="text-primary-main"
                    />
                  }
                  rightContent={
                    checkOutDate ? (
                      <ButtonIcon
                        variant="text"
                        color="gray"
                        size="sm"
                        icon={IconNames.CIRCLE_X}
                        iconSize="lg"
                        ariaDescribedby="clear check out date input"
                        onClick={() => {
                          setCheckOutDate && setCheckOutDate(null);
                          setCheckOutDate(null);
                          setDateRangePickerFocusedInput('endDate');
                        }}
                        className="ps-0 pe-0"
                      />
                    ) : (
                      <ButtonIcon
                        variant="text"
                        // @ts-ignore
                        color={'white'}
                        size="sm"
                        className="ps-0 pe-0"
                      />
                    )
                  }
                  autoComplete="off"
                  hasCaret={false}
                />
              </div>
            </div>
            <div className="absolute z-50 top-[7.15rem] -left-2">
              <Dropdown
                dropdownWidth={'635px'}
                reference={dateRangePickerInputRef.current}
                isOpen={!!dateRangePickerFocusedInput}
                onClickAway={() => {
                  setDateRangePickerFocusedInput(null);
                }}
                data-id="date-range-picker-dropdown"
              >
                <div className="px-2">
                  <DayPickerComponent
                    verticalBorderSpacing={0}
                    enableOutsideDays={false}
                    minDate={null}
                    noBorder={true}
                    minimumNights={1}
                    numberOfMonths={2}
                    initialVisibleMonth={null}
                    focusedInput={dateRangePickerFocusedInput}
                    onDatesChange={handleDatesChange}
                    onFocusChange={(
                      dateRangePickerFocusedInput: FocusedInputShape | null
                    ) => {
                      setDateRangePickerFocusedInput(
                        dateRangePickerFocusedInput
                      );
                    }}
                    startDate={checkInDate}
                    endDate={checkOutDate}
                    isOutsideRange={(day: moment.Moment) =>
                      !isInclusivelyAfterDay(day, moment())
                    }
                    hideKeyboardShortcutsPanel={true}
                  />
                </div>
                <div className="flex w-full justify-end pb-2 pr-2">
                  <Button
                    variant="text"
                    className="bg-white"
                    onClick={() => {
                      setCheckInDate && setCheckInDate(null);
                      setCheckInDate(null);
                      setCheckOutDate && setCheckOutDate(null);
                      setCheckOutDate(null);
                      setDateRangePickerFocusedInput('startDate');
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </Dropdown>
            </div>

            <div className="flex space-x-2">
              <Button
                data-testid={'@filters/filters-btn'}
                onClick={activateFeaturesModal}
                variant="outlined"
                color="primary"
                size="lg"
                className="focus:outline-none"
              >
                <Icon name={IconNames.FILTER} size="sm" />
                <span>Filters</span>
              </Button>

              {/* Room Filters */}
              <Button
                data-testid={'@filters/rooms-dropdown-btn'}
                onClick={() => setRoomFocusedInput(!roomFocusedInput)}
                variant="outlined"
                color="primary"
                size="lg"
              >
                <Icon
                  name={
                    roomFocusedInput
                      ? IconNames.CHEVRON_UP
                      : IconNames.CHEVRON_DOWN
                  }
                  size="sm"
                />
                <span>Rooms</span>
              </Button>

              <div className="absolute z-50 top-[7.15rem] align-center">
                <Dropdown
                  dropdownWidth={'275px'}
                  reference={roomInputRef.current}
                  isOpen={roomFocusedInput}
                  onClickAway={() => {
                    setRoomFocusedInput(false);
                  }}
                  data-id="guest-count-picker-dropdown"
                  className="p-4 divide-y divide-gray-light inset-0 items-start bg-white"
                >
                  <div className="flex justify-between items-center pb-2">
                    <Typography variant="subtitle2" component="p">
                      Bedrooms
                    </Typography>

                    <div className="flex space-x-2 justify-between">
                      <ButtonIcon
                        icon={IconNames.MINUS}
                        ariaDescribedby="remove bedroom"
                        variant="text"
                        className="text-gray-main border-gray-main"
                        onClick={() => {
                          setMinNumberOfRooms((val) => {
                            if (val > 0) {
                              return val - 1;
                            } else {
                              return val;
                            }
                          });
                          resetPagination();
                        }}
                      />
                      <input
                        type="number"
                        className="w-6 text-center"
                        value={minNumberOfRooms}
                      />
                      <ButtonIcon
                        icon={IconNames.PLUS}
                        ariaDescribedby="add bedroom"
                        variant="text"
                        className="text-gray-main border-gray-main"
                        onClick={() => {
                          setMinNumberOfRooms((val) => {
                            return val + 1;
                          });
                          resetPagination();
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <Typography variant="subtitle2" component="p">
                      Bathrooms
                    </Typography>

                    <div className="flex space-x-2 justify-between">
                      <ButtonIcon
                        icon={IconNames.MINUS}
                        ariaDescribedby="remove bathroom"
                        variant="text"
                        className="text-gray-main border-gray-main"
                        onClick={() => {
                          setMinNumberOfBathrooms((val) => {
                            if (val > 0) {
                              return val - 1;
                            } else {
                              return val;
                            }
                          });
                          resetPagination();
                        }}
                      />
                      <input
                        type="number"
                        className="w-6 text-center"
                        value={minNumberOfBathrooms}
                      />
                      <ButtonIcon
                        icon={IconNames.PLUS}
                        ariaDescribedby="add bathroom"
                        variant="text"
                        className="text-gray-main border-gray-main"
                        onClick={() => {
                          setMinNumberOfBathrooms((val) => {
                            return val + 1;
                          });
                          resetPagination();
                        }}
                      />
                    </div>
                  </div>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 sm:pt-4 pl-2 w-full items-center justify-left align-middle flex">
          <div className="lg:hidden flex items-end whitespace-nowrap pr-4 text-primary-main">
            <Button
              onClick={activateFeaturesModal}
              variant="outlined"
              color="primary"
              size="sm"
              className="focus:outline-none"
            >
              <Icon name={IconNames.FILTER} size="sm" />
              <span>Filters</span>
            </Button>
          </div>

          <div className="flex lg:hidden pr-2">
            <Link
              to={routes.accountListingsSearchShow({
                slug: accountSlug,
                checkInDate: checkInDate?.format('YYYY-MM-DD'),
                checkOutDate: checkOutDate?.format('YYYY-MM-DD'),
                bathroomCount: minNumberOfBathrooms,
                bedroomCount: minNumberOfRooms,
                activePropertyTypes: propertyTypes
                  .map((propertyType) => propertyType)
                  .join(','),
              })}
              aria-label="Go to Map View"
              variant="outlined"
              color="gray"
              size="sm"
            >
              <Icon
                name={IconNames.MAP}
                size="lg"
                className="text-primary-main"
              />
            </Link>
          </div>
        </div>

        <div className="flex flex-col">
          {listingsLoading ? (
            <div className="mt-16 items-center text-center w-full">
              <Spinner size={SpinnerSize.SIZE_LARGE} />
            </div>
          ) : (
            <>
              {listingsQueryResults?.listingsSearch.entries &&
                listingsQueryResults?.listingsSearch.entries.length > 0 && (
                  <>
                    <div className="sm:mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4 lg:gap-8">
                      {listingsQueryResults.listingsSearch.entries.map(
                        (listing) => (
                          <div key={listing.slug}>
                            <ListingCard
                              listing={listing}
                              altTag={`${listing.name || ''}`}
                              data-testid={concatenateAndReplace(
                                `${listing.name}`
                              )}
                              accountShowRoute={routes.accounts(
                                listing.account.slug || ''
                              )}
                              avatarUrl={listing.account.avatarUrl}
                              showRoute={routes.listings({
                                slug: listing.slug,
                                checkInDate: checkInDate?.format('YYYY-MM-DD'),
                                checkOutDate:
                                  checkOutDate?.format('YYYY-MM-DD'),
                              })}
                              forceMobileView={true}
                              isFavorited={listing.isFavorited}
                              onFavoritedSuccess={() => {}}
                            />
                          </div>
                        )
                      )}
                    </div>
                    <div className="flex my-4 justify-center sm:mt-8 md:justify-end">
                      <nav className="flex w-full text-center justify-center space-x-4">
                        <Button
                          variant="outlined"
                          color="primary"
                          className={cx('border-[1.5px]', {
                            'hover:cursor-not-allowed':
                              !listingsQueryResults.listingsSearch.metadata
                                .cursorBefore,
                          })}
                          onClick={() => {
                            prevPage();
                            scrollToTop();
                          }}
                          disabled={
                            !listingsQueryResults.listingsSearch.metadata
                              .cursorBefore
                          }
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          className={cx('border-[1.5px]', {
                            'hover:cursor-not-allowed':
                              !listingsQueryResults.listingsSearch.metadata
                                .cursorAfter,
                          })}
                          onClick={() => {
                            nextPage();
                            scrollToTop();
                          }}
                          disabled={
                            !listingsQueryResults.listingsSearch.metadata
                              .cursorAfter
                          }
                        >
                          Next
                        </Button>
                      </nav>
                    </div>
                  </>
                )}

              {listingsQueryResults?.listingsSearch.entries.length === 0 && (
                <div className="pl-4 sm:pl-8 pt-8 sm:py-12 w-full text-primary-main">
                  <div className="text-xl font-semibold">NO RESULTS</div>
                  <div className="text-lg font-medium pt-6">
                    To get more results, try adjusting your search.
                  </div>

                  <div className="mt-6 items-center space-y-4 flex flex-col xs:flex-row xs:space-x-4 xs:space-y-0">
                    {(checkInDate || checkOutDate) && (
                      <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={clearDates}
                      >
                        Clear Dates
                      </Button>
                    )}

                    {hasFiltersApplied && (
                      <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={resetFilters}
                      >
                        Reset Filters
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
